import React from 'react';
import { compose } from 'redux';
import { LocalizedMessage, LocalizedStandardDate } from 'intl/LocalizedComponents';
import classnames from 'classnames';
import _map from 'lodash/map';
import _find from 'lodash/find';
import _isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
// Core components
import Badge from 'components/core/Badge';
import TableRow from 'components/core/table/TableRow';
import TableCell from 'components/core/table/TableCell';
import Tooltip from 'components/core/Tooltip';
// Colors
import { iconPalette } from 'components/core/colors';
// Permissions
import permissions from 'constants/Permissions';
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import { networkLevelIcon, networkAccessTechnology } from 'models/device';
import { desc as deviceModelDesc } from 'models/deviceModel';
import {
  icon as distributionIcon,
  desc as distributionDesc,
  fullDesc as distributionFullDesc,
} from 'models/distribution';
import { desc as operatorDesc } from 'models/operator';
import { icon as rolloutIcon } from 'models/rollout';
// Custom
import { listRowSpecsShape, deviceDataShape } from 'constants/PropTypesShapes';
import { showPath } from 'devices/navigation';
import withDeviceData from 'appAssets/hocs/WithDeviceData';
import withLoading from 'hocs/WithLoading';
import SimcardLink from 'simcards/assets/main/SimcardLink';
import InstallationLink from 'installations/assets/main/InstallationLink';
import DeviceStatusWidget from 'devices/assets/main/DeviceStatusWidget';
import CompanyIdLink from 'companies/assets/main/CompanyIdLink';
import DeviceBatchIdLink from 'production/assets/deviceBatch/DeviceBatchIdLink';
import AppIcon from 'components/icons/AppIcon';
import { ShortcutIconButton, EditIconButton, DeleteIconButton } from 'components/buttons/IconButtons';
import TruncatedTooltipText from 'components/misc/TruncatedTooltipText';

const DeviceDistributions = ({ deviceData }) => {
  const { device_distribution_maps: deviceDistributionMaps } = deviceData;

  // Find OPAC distribution
  const opacDistribution = _find(deviceDistributionMaps, ['type', 'opac']);

  if (opacDistribution) {
    return (
      <Tooltip
        content={(
          <div>
            {_map(deviceDistributionMaps, (distribution) => (
              <div key={distribution.id} className="row m-0">
                {distributionFullDesc(distribution)}
              </div>
            ))}
          </div>
        )}
      >
        <Badge content={distributionDesc(opacDistribution)} backgroundColor={iconPalette.base} type="small" gap="large">
          <AppIcon defaultColor id={distributionIcon} />
        </Badge>
      </Tooltip>
    );
  }
  return null;
};

DeviceDistributions.propTypes = {
  deviceData: deviceDataShape.isRequired,
};

const DeviceNetwork = ({ deviceData }) => {
  const { info, ip_address: ipAddress } = deviceData;

  if (info) {
    const {
      network_access_technology: networkAccessTechnologyDesc,
      network_level: networkLevel,
      network_operator: networkOperator,
    } = info;
    if (!_isNil(networkLevel)) {
      const { icon, color } = networkLevelIcon(networkLevel);
      const { icon: networkAccessTechnologyIcon } = networkAccessTechnology(networkAccessTechnologyDesc) || {};
      return (
        <div className="d-flex align-items-center">
          <Tooltip
            content={(
              <div className="row" style={{ minWidth: 250 }}>
                <div className="col-6 text-right pr-2">
                  <em>
                    <LocalizedMessage id="field.networkLevel" />
                  </em>
                </div>
                <div className="col-6 text-left pl-2">{`${networkLevel}/5`}</div>
                <div className="col-6 text-right pr-2">
                  <em>
                    <LocalizedMessage id="field.networkAccessTechnology" />
                  </em>
                </div>
                <div className="col-6 text-left pl-2">
                  {!_isNil(networkAccessTechnologyDesc) ? (
                    `${networkAccessTechnologyDesc}`
                  ) : (
                    <LocalizedMessage id="info.unknown" values={{ gender: 'female' }} />
                  )}
                </div>
                <div className="col-6 text-right pr-2">
                  <em>
                    <LocalizedMessage id="field.ipAddress" />
                  </em>
                </div>
                <div className="col-6 text-left pl-2">
                  {ipAddress || <LocalizedMessage id="info.unknown" values={{ gender: 'female' }} />}
                </div>
              </div>
            )}
          >
            <AppIcon id={icon} color={color} className={classnames({ 'mr-2': networkAccessTechnologyIcon })} />
            {networkAccessTechnologyIcon && (
              <AppIcon
                id={networkAccessTechnologyIcon}
                defaultColor
                fontSize="small"
                className="mr-2"
                style={{ marginTop: '-4px' }}
              />
            )}
          </Tooltip>
          {networkOperator && <TruncatedTooltipText>{operatorDesc(networkOperator)}</TruncatedTooltipText>}
        </div>
      );
    }
  }
  return null;
};

DeviceNetwork.propTypes = {
  deviceData: deviceDataShape.isRequired,
};

const DeviceRow = React.forwardRef(
  ({
    device, deviceData, rowSpecs, withLink, withCompany, handleStartLoading, onDelete,
  }, ref) => {
    const { id: deviceId } = device;

    // Handlers
    const handleDelete = () => {
      onDelete(deviceData);
    };

    if (deviceData) {
      return (
        <TableRow
          ref={ref}
          element={device}
          rowSpecs={rowSpecs}
          actions={
            withLink
              ? [<ShortcutIconButton key="bLink" primary path={showPath(deviceId)} />]
              : [
                <EditIconButton key="bEdit" onClick={handleStartLoading} path={showPath(deviceId)} />,
                <OnlyWith
                  key="bDelete"
                  permissions={{
                    needed: permissions.pDevice.write,
                    permissionsOrContext: 'devicePermissions',
                  }}
                >
                  <DeleteIconButton onClick={handleDelete} />
                </OnlyWith>,
              ]
          }
        >
          <TableCell className={classnames({ 'table-first-details': rowSpecs.withDetails })}>
            <div className="d-flex align-items-center">
              <TruncatedTooltipText>{deviceData.serial}</TruncatedTooltipText>
              {deviceData.rollout_in_progress && (
                <OnlyWith
                  permissions={{
                    needed: permissions.pAdmin,
                  }}
                >
                  <Tooltip content={<LocalizedMessage id="tooltip.rolloutInProgress" />}>
                    <AppIcon primary id={rolloutIcon} className="ml-2" style={{ width: 20, height: 20 }} />
                  </Tooltip>
                </OnlyWith>
              )}
            </div>
          </TableCell>
          <TableCell className="hidden-md-down">
            <TruncatedTooltipText>{deviceModelDesc(deviceData.device_model)}</TruncatedTooltipText>
          </TableCell>
          <OnlyWith
            permissions={{
              needed: permissions.pAdmin,
            }}
          >
            <TableCell className="hidden-md-down">
              <DeviceDistributions deviceData={deviceData} />
            </TableCell>
          </OnlyWith>
          <TableCell className="table-content-1" noPadding>
            <DeviceStatusWidget device={deviceData} source="device" parentId={deviceId} />
          </TableCell>
          <TableCell className="hidden-xs-down">
            <TruncatedTooltipText>
              {deviceData.last_comm_date && <LocalizedStandardDate value={deviceData.last_comm_date} />}
            </TruncatedTooltipText>
          </TableCell>
          <TableCell className="hidden-xs-down">
            <DeviceNetwork deviceData={deviceData} />
          </TableCell>
          <TableCell className="hidden-md-down">
            {deviceData.installation && <InstallationLink installation={deviceData.installation} />}
          </TableCell>
          <TableCell className="hidden-md-down">
            {deviceData.simcard && <SimcardLink simcard={deviceData.simcard} />}
          </TableCell>
          <OnlyWith
            permissions={{
              needed: permissions.pAdmin,
            }}
          >
            <TableCell className="hidden-lg-down">
              {deviceData.device_production_batch_id && (
                <DeviceBatchIdLink deviceBatchId={deviceData.device_production_batch_id} />
              )}
            </TableCell>
          </OnlyWith>
          {withCompany && (
            <OnlyWith
              permissions={{
                needed: permissions.pAdmin,
              }}
            >
              <TableCell className="hidden-lg-down">
                <CompanyIdLink companyId={deviceData.company_id} />
              </TableCell>
            </OnlyWith>
          )}
        </TableRow>
      );
    }
    return null;
  },
);

DeviceRow.displayName = 'DeviceRow';

DeviceRow.propTypes = {
  device: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
  deviceData: deviceDataShape,
  rowSpecs: listRowSpecsShape.isRequired,
  withLink: PropTypes.bool,
  withCompany: PropTypes.bool,
  handleStartLoading: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

DeviceRow.defaultProps = {
  deviceData: null,
  withLink: false,
  withCompany: true,
  onDelete: null,
};

module.exports = compose(withDeviceData('device.id'), withLoading)(DeviceRow);



// WEBPACK FOOTER //
// ./routes/app/routes/devices/assets/main/DeviceRow.js